import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress, Button, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import {
    useRecordContext,
    useTranslate,
    SimpleForm,
    useDataProvider,
    useNotify,
    useRefresh,
    usePermissions,
    useGetIdentity,
    useGetOne,
    useGetMany,
} from 'react-admin';
import UserAdminAvatarIcon from '@/assets/icons/user-avatar-yellow-icon.svg?react';
import UserSerwisAvatarIcon from '@/assets/icons/user-avatar-green-icon.svg?react';
import SystemNoteIcon from '@/assets/icons/system-info-icon.svg?react';
import HiddenIcon from '@/assets/icons/eye-hidden-icon.svg?react';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import DeleteIcon from '@/assets/icons/delete-icon.svg?react';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import CommentInput from '@/components/service/activity/CommentInput.jsx';
import { activityStyles } from '@/themes/commonStyles/activity.js';
import { serviceRequestStatusChoices, serviceRequestPriorityChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import ArrowRightSmallIcon from '@/assets/icons/arrow-right-small-icon.svg?react';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { findChoiceById } from '@/helpers/format/findChoiceById.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import useCommentImages from '@/composables/useCommentImages';
import useActivityCommentEdit from '@/composables/useActivityCommentEdit.js';
import CommentImageThumbnail from '@/components/service/activity/CommentImageThumbnail.jsx';
import CommentImageSlider from '@/components/service/activity/CommentImageSlider.jsx';
import StatusColorBox from '@/components/global/fields/StatusColorBox.jsx';
import { CHANGE_TYPES, USER_ROLES } from '@/constants/ActivitiesConfig.js';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import DisactiveIcon from '@/assets/icons/disactive-icon.svg?react';

const ActivityStatusChange = ({ record, previousStatus, currentStatus, translate, translatePrefix }) => {
    if (record.changeType !== CHANGE_TYPES.STATUS) return null;

    return (
        <Typography className='details-content'>
            {translate(`${translatePrefix}.statusChange`)}:
            <Box className='status-pill-container'>
                {previousStatus && (
                    <Box component='span' className='status-pill status-pill--previous'>
                        {previousStatus.icon}
                        <span>{previousStatus.name}</span>
                    </Box>
                )}
                <ArrowRightSmallIcon />
                {currentStatus && (
                    <Box component='span' className='status-pill'>
                        {currentStatus.icon}
                        <span>{currentStatus.name}</span>
                    </Box>
                )}
            </Box>
        </Typography>
    );
};

ActivityStatusChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string,
    }).isRequired,
    previousStatus: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
    }),
    currentStatus: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
    }),
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
};

const ActivityTypeChange = ({ record, previousType, currentType, translate, translatePrefix }) => {
    if (record.changeType !== CHANGE_TYPES.TYPE) return null;

    return (
        <Typography className='details-content'>
            {translate(`${translatePrefix}.typeChange`)}:
            <Box className='status-pill-container'>
                {previousType && (
                    <Box component='span' className='status-pill status-pill--previous'>
                        <StatusColorBox backgroundColor={Palette.gray.gray75} outlineColor={Palette.gray.gray200}>
                            {previousType.icon}
                        </StatusColorBox>
                        <span>{previousType.name}</span>
                    </Box>
                )}
                <ArrowRightSmallIcon />
                {currentType && (
                    <Box component='span' className='status-pill'>
                        <StatusColorBox backgroundColor={currentType.color} outlineColor={currentType.outlineColor}>
                            {currentType.icon}
                        </StatusColorBox>
                        <span>{currentType.name}</span>
                    </Box>
                )}
            </Box>
        </Typography>
    );
};

ActivityTypeChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string,
    }).isRequired,
    previousType: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
    }),
    currentType: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        outlineColor: PropTypes.string.isRequired,
    }),
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
};

const ActivityPriorityChange = ({ record, previousPriority, currentPriority, translate, translatePrefix }) => {
    if (record.changeType !== CHANGE_TYPES.PRIORITY) return null;

    return (
        <Typography className='details-content' sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {translate(`${translatePrefix}.priorityChange`)}:
            {previousPriority && (
                <Box component='span' className='status-pill status-pill--previous'>
                    <StatusColorBox backgroundColor={Palette.gray.gray75} outlineColor={Palette.gray.gray200}>
                        {previousPriority.icon}
                    </StatusColorBox>
                    <span>{previousPriority.name}</span>
                </Box>
            )}
            <ArrowRightSmallIcon />
            {currentPriority && (
                <Box component='span' className='status-pill'>
                    <StatusColorBox backgroundColor={currentPriority.color} outlineColor={currentPriority.outlineColor}>
                        {currentPriority.icon}
                    </StatusColorBox>
                    <span>{currentPriority.name}</span>
                </Box>
            )}
        </Typography>
    );
};

ActivityPriorityChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string,
    }).isRequired,
    previousPriority: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
    }),
    currentPriority: PropTypes.shape({
        icon: PropTypes.node.isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        outlineColor: PropTypes.string.isRequired,
    }),
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
};

const formatScheduledDate = value => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ActivityScheduledDateChange = ({ record, translate, translatePrefix }) => {
    if (record.changeType !== CHANGE_TYPES.SCHEDULED_DATE) return null;

    return (
        <Typography className='details-content' sx={{ display: 'flex', alignItems: 'center' }}>
            {translate(`${translatePrefix}.scheduledDateChange`)}: <span>{formatScheduledDate(record.prevValue)}</span> <ArrowRightSmallIcon />{' '}
            <span>{formatScheduledDate(record.value)}</span>
        </Typography>
    );
};

ActivityScheduledDateChange.propTypes = {
    translatePrefix: PropTypes.string.isRequired,
    record: PropTypes.shape({
        changeType: PropTypes.string.isRequired,
        prevValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    }).isRequired,
    translate: PropTypes.func.isRequired,
};

const ActivityDifficultyChange = ({ record, translate, translatePrefix }) => {
    if (record.changeType !== CHANGE_TYPES.DIFFICULTY) return null;

    return (
        <Typography className='details-content' sx={{ display: 'flex', alignItems: 'center' }}>
            {translate(`${translatePrefix}.difficultyChange`)}: {record.prevValue ? record.prevValue.toString() : '-'} <ArrowRightSmallIcon />{' '}
            {record.value ? record.value.toString() : '-'}
        </Typography>
    );
};

ActivityDifficultyChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string.isRequired,
        prevValue: PropTypes.number,
        value: PropTypes.number,
    }).isRequired,
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
};

const ActivityCreatedAtChange = ({ record, requestData, translate, translatePrefix, isServiceInspection }) => {
    if (record.changeType !== CHANGE_TYPES.CREATED_AT) return null;

    const sourceType = isServiceInspection ? record.serviceInspection?.sourceType : record.serviceRequest?.sourceType;
    if (sourceType === 'auto') {
        return (
            <Typography className='details-content'>
                {`${translate(`${translatePrefix}.createdAutomatically`)}:`}
                <br />
                {requestData?.error?.name && (
                    <>
                        {translate(`${translatePrefix}.error`)} {requestData?.error?.name}
                    </>
                )}
            </Typography>
        );
    }
    if (sourceType === 'user') {
        return (
            <Typography className='details-content'>
                <p>
                    {`${translate(`${translatePrefix}.createdManually`)}:`}
                    <br />
                    {record.user?.firstName} {record.user?.lastName}
                </p>
                {requestData?.description && (
                    <p>
                        {translate(`${translatePrefix}.description`)}:
                        <br />
                        <span className='details-content' dangerouslySetInnerHTML={{ __html: requestData.description }} />
                    </p>
                )}
            </Typography>
        );
    }
    if (sourceType === 'collection_point') {
        return (
            <Typography className='details-content'>
                {`${translate(`${translatePrefix}.createdAutomatically`)}:`}
                <br />
                {translate(`${translatePrefix}.collectionPointEmployee`)}
            </Typography>
        );
    }
    return null;
};

ActivityCreatedAtChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string.isRequired,
        serviceRequest: PropTypes.shape({
            sourceType: PropTypes.string,
        }),
        user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
    }).isRequired,
    requestData: PropTypes.shape({
        error: PropTypes.shape({
            name: PropTypes.string,
        }),
        description: PropTypes.string,
    }),
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
    isServiceInspection: PropTypes.bool.isRequired,
};

const ActivityPersonAssignment = ({ record, changeType, changedLabel, assignedLabel, previouslyAssignedPerson, currentlyAssignedPerson }) => {
    if (record.changeType !== changeType) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography className='details-content'>{previouslyAssignedPerson ? changedLabel : assignedLabel}</Typography>
            {previouslyAssignedPerson && <Typography className='details-content'>{previouslyAssignedPerson}</Typography>}
            {previouslyAssignedPerson && currentlyAssignedPerson && <ArrowRightSmallIcon />}
            {currentlyAssignedPerson && <Typography className='details-content'>{currentlyAssignedPerson}</Typography>}
        </Box>
    );
};

ActivityPersonAssignment.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string.isRequired,
    }).isRequired,
    changeType: PropTypes.string.isRequired,
    changedLabel: PropTypes.string.isRequired,
    assignedLabel: PropTypes.string.isRequired,
    previouslyAssignedPerson: PropTypes.string,
    currentlyAssignedPerson: PropTypes.string,
};

const ActivityUsedPartsChange = ({ record, translate, translatePrefix }) => {
    const prev = record.prevValue ? JSON.parse(record.prevValue) : [];
    const current = record.value ? JSON.parse(record.value) : [];

    const added = current.filter(x => !prev.includes(x));
    const removed = prev.filter(x => !current.includes(x));
    const changed = [...added, ...removed];

    const { data, isLoading } = useGetMany('spare-part', { ids: changed }, { enabled: changed.length > 0 });
    const partsById = Object.fromEntries((data ?? []).map(part => [part.id, part.name]));

    return (
        <Typography className='details-content'>
            {translate(`${translatePrefix}.usedPartsChange`)}:
            <Box className='status-pill-container'>
                {added.map(id => (
                    <UsedPartDiffItem key={id} id={id} name={partsById[id]} type='added' isLoading={isLoading} />
                ))}
                {removed.map(id => (
                    <UsedPartDiffItem key={id} id={id} name={partsById[id]} type='removed' isLoading={isLoading} />
                ))}
            </Box>
        </Typography>
    );
};

ActivityUsedPartsChange.propTypes = {
    record: PropTypes.shape({
        changeType: PropTypes.string.isRequired,
        prevValue: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    }).isRequired,
    translate: PropTypes.func.isRequired,
    translatePrefix: PropTypes.string.isRequired,
};

const UsedPartDiffItem = ({ id, name, type, isLoading }) => (
    <Box component='span' className={`status-pill status-pill--${type}`} {...(isLoading && { sx: { minWidth: '80px', minHeight: '20px' } })}>
        {(type === 'added' && <ActiveIcon />) || <DisactiveIcon />}
        <span>{isLoading || name || id}</span>
    </Box>
);

UsedPartDiffItem.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    type: PropTypes.oneOf(['added', 'removed']).isRequired,
    isLoading: PropTypes.bool.isRequired,
};

const ActivityListItem = ({ activeCommentId, onToggleActions, resource, elementId }) => {
    const record = useRecordContext();
    const isServiceInspection = resource === 'service-inspection' ? true : globalThis.location.pathname.includes('/service-inspection');
    const resolvedParentId = isServiceInspection ? record?.serviceInspectionId ?? elementId : record?.serviceRequestId ?? elementId;
    const translatePrefix = `resources.${isServiceInspection ? 'serviceInspection' : 'serviceRequest'}.activityList`;
    const { data: requestData } = useGetOne(resource, { id: resolvedParentId }, { enabled: !!resolvedParentId });
    const { permissions } = usePermissions();
    const { identity } = useGetIdentity();
    const [openConfirm, setOpenConfirm] = useState(false);
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imageModalIndex, setImageModalIndex] = useState(0);
    const normalizeImageName = useCallback(image => {
        if (typeof image === 'string') return image;
        if (image?.imageName) return image.imageName;
        if (image?.name) return image.name;
        if (image?.fileName) return image.fileName;
        if (image?.filename) return image.filename;
        return image;
    }, []);

    const imageNames = useMemo(() => {
        if (!record?.images?.length) return [];
        return record.images.map(normalizeImageName).filter(Boolean);
    }, [record?.images, normalizeImageName]);

    const { imageBlobUrls } = useCommentImages({
        serviceRequestId: isServiceInspection ? null : resolvedParentId,
        serviceInspectionId: isServiceInspection ? resolvedParentId : null,
        changeId: record?.id,
        images: imageNames,
        useThumbnails: true,
        resource,
    });

    const areAllImagesLoaded = useMemo(() => {
        if (!imageNames.length) return true;
        return imageNames.every((_, index) => Boolean(imageBlobUrls[index]));
    }, [imageNames, imageBlobUrls]);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const isAdmin = permissions.isAdmin;
    const isServiceMaster = permissions.isServiceMaster;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const previousStatus = useMemo(() => findChoiceById(serviceRequestStatusChoices(translate), record.prevValue), [record.prevValue, translate]);
    const currentStatus = useMemo(() => findChoiceById(serviceRequestStatusChoices(translate), record.value), [record.value, translate]);
    const previousPriority = useMemo(() => findChoiceById(serviceRequestPriorityChoices(translate), record.prevValue), [record.prevValue, translate]);
    const currentPriority = useMemo(() => findChoiceById(serviceRequestPriorityChoices(translate), record.value), [record.value, translate]);
    const previousType = useMemo(() => findChoiceById(serviceRequestTypeChoices(translate), record.prevValue), [record.prevValue, translate]);
    const currentType = useMemo(() => findChoiceById(serviceRequestTypeChoices(translate), record.value), [record.value, translate]);

    const previouslyAssignedPerson = useMemo(
        () => (record.prevValueRelation ? `${record.prevValueRelation.firstName} ${record.prevValueRelation.lastName}` : null),
        [record.prevValueRelation],
    );
    const currentlyAssignedPerson = useMemo(
        () => (record.valueRelation ? `${record.valueRelation.firstName} ${record.valueRelation.lastName}` : null),
        [record.valueRelation],
    );

    const { isEditing, handleStartEdit, handleCancelEdit, handleSaveEdit } = useActivityCommentEdit({
        record,
        isMobile,
        onToggleActions,
    });

    const userRoleIcon = useMemo(() => {
        if (record?.user && [USER_ROLES.ADMIN, USER_ROLES.BOK, USER_ROLES.SERVICE_MASTER].includes(record.user.role)) {
            return <UserAdminAvatarIcon />;
        }
        if (record?.user && [USER_ROLES.SERVICE].includes(record.user.role)) {
            return <UserSerwisAvatarIcon />;
        }
        return null;
    }, [record?.user]);

    const activityListClass = useMemo(() => {
        if (!record) return 'activity-list-item';
        const isYellowType = [
            CHANGE_TYPES.DESCRIPTION,
            CHANGE_TYPES.STATUS,
            CHANGE_TYPES.TYPE,
            CHANGE_TYPES.PRIORITY,
            CHANGE_TYPES.WORKING_TIME,
            CHANGE_TYPES.CREATED_AT,
            CHANGE_TYPES.COORDINATOR_ID,
            CHANGE_TYPES.SERVICEMAN_ID,
            CHANGE_TYPES.MIGRATED_NOTE,
            CHANGE_TYPES.SCHEDULED_DATE,
            CHANGE_TYPES.DIFFICULTY,
            CHANGE_TYPES.USED_PARTS,
        ].includes(record.changeType);

        return `activity-list-item ${record.hidden ? 'activity-list-item--hidden' : ''} ${openConfirm ? 'opacity-20' : ''} ${
            isYellowType ? 'activity-list-item--yellow' : ''
        }`;
    }, [record, openConfirm]);

    const handleDeleteItem = useCallback(async () => {
        if (!record) return;
        setIsLoadingDelete(true);
        try {
            await dataProvider.deleteOne(resource, { id: resolvedParentId, changeId: record.id });
        } catch (err) {
            notify(err.message, { type: 'error' });
        } finally {
            refresh();
            setIsLoadingDelete(false);
        }
    }, [dataProvider, record, notify, refresh, resource, resolvedParentId]);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
    }, []);

    const handleOpenConfirm = useCallback(() => {
        setOpenConfirm(true);
    }, []);

    const renderActionButtons = useCallback(() => {
        if (!record || record.changeType !== CHANGE_TYPES.COMMENT || isEditing) return null;

        return (
            <Box className='action-buttons' data-visible={isMobile && activeCommentId === record.id ? 'true' : undefined}>
                {identity?.id === record.user?.email && (
                    <Button variant='contained' className='button--icon-only white button--edit' onClick={handleStartEdit} disabled={!areAllImagesLoaded}>
                        <EditIcon />
                    </Button>
                )}
                {isAdmin && (
                    <Button
                        variant='contained'
                        className='button--icon-only white button--delete'
                        onClick={event => {
                            event.stopPropagation();
                            handleOpenConfirm();
                        }}
                    >
                        <DeleteIcon />
                    </Button>
                )}
            </Box>
        );
    }, [record, isEditing, isAdmin, identity?.id, isMobile, activeCommentId, handleStartEdit, handleOpenConfirm, areAllImagesLoaded]);

    const renderCommentImages = useCallback(() => {
        if (!record || record.changeType !== CHANGE_TYPES.COMMENT || !imageNames.length) return null;

        const handleImageClick = index => {
            setImageModalIndex(index);
            setImageModalOpen(true);
        };

        const imagesData = imageNames.map(imageName => ({
            serviceRequestId: isServiceInspection ? null : resolvedParentId,
            serviceInspectionId: isServiceInspection ? resolvedParentId : null,
            changeId: record.id,
            imageName,
        }));

        return (
            <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
                    {imageNames.map((imageName, index) => (
                        <CommentImageThumbnail key={imageName} blobUrl={imageBlobUrls[index]} onClick={() => handleImageClick(index)} />
                    ))}
                </Box>
                <CommentImageSlider
                    open={imageModalOpen}
                    onClose={() => setImageModalOpen(false)}
                    images={imagesData}
                    initialIndex={imageModalIndex}
                    serviceRequestId={isServiceInspection ? null : resolvedParentId}
                    serviceInspectionId={isServiceInspection ? resolvedParentId : null}
                    blobUrls={imageBlobUrls}
                    resource={resource}
                />
            </>
        );
    }, [record, imageNames, imageBlobUrls, imageModalOpen, imageModalIndex, isServiceInspection, resolvedParentId, resource]);

    const renderCommentEdit = useCallback(() => {
        if (!record) return null;
        const existingImageBlobUrls = imageNames.length ? imageNames.map((imageName, index) => imageBlobUrls[index]).filter(Boolean) : [];
        const existingImageNames = imageNames;

        return (
            <SimpleForm toolbar={null} defaultValues={{ value: record.value }} onSubmit={handleSaveEdit}>
                <Box sx={{ width: '100%' }}>
                    <CommentInput
                        className='service-request-rich-text-input'
                        isEditingComment={true}
                        isHidden={record.hidden}
                        source='value'
                        serviceRequestId={isServiceInspection ? null : resolvedParentId}
                        serviceInspectionId={isServiceInspection ? resolvedParentId : null}
                        changeId={record.id}
                        resource={resource}
                        onCancelEdit={handleCancelEdit}
                        onEditSuccess={handleCancelEdit}
                        existingImageBlobUrls={existingImageBlobUrls}
                        existingImageNames={existingImageNames}
                        fullWidth
                    />
                </Box>
            </SimpleForm>
        );
    }, [record, imageNames, imageBlobUrls, handleSaveEdit, handleCancelEdit, isServiceInspection, resolvedParentId, resource]);

    const renderCommentRead = useCallback(() => {
        if (!record) return null;

        return (
            <>
                <Typography className='details-content' dangerouslySetInnerHTML={{ __html: record.value }} />
                {renderCommentImages()}
            </>
        );
    }, [record, renderCommentImages]);

    const renderDescriptionChange = useCallback(() => {
        if (!record) return null;
        return <Typography className='details-content' dangerouslySetInnerHTML={{ __html: record.value }} />;
    }, [record]);

    const renderMigratedNote = useCallback(() => {
        if (!record) return null;
        return (
            <Typography
                className='details-content'
                dangerouslySetInnerHTML={{ __html: `<p>${translate(`${translatePrefix}.migratedNote`)}:</p>${record.value}` }}
            />
        );
    }, [record, translatePrefix, translate]);

    const renderWorkingTimeChange = useCallback(() => {
        if (!record) return null;
        return (
            <Typography className='details-content' sx={{ display: 'flex', alignItems: 'center' }}>
                {translate(`${translatePrefix}.workingTimeChange`)}: {record.prevValue}h <ArrowRightSmallIcon /> {record.value}h
            </Typography>
        );
    }, [record, translatePrefix, translate]);

    const renderOtherChanges = useCallback(() => {
        if (!record) return null;
        return (
            <>
                <ActivityStatusChange
                    record={record}
                    previousStatus={previousStatus}
                    currentStatus={currentStatus}
                    translate={translate}
                    translatePrefix={translatePrefix}
                />
                <ActivityTypeChange
                    record={record}
                    previousType={previousType}
                    currentType={currentType}
                    translate={translate}
                    translatePrefix={translatePrefix}
                />
                <ActivityPriorityChange
                    record={record}
                    previousPriority={previousPriority}
                    currentPriority={currentPriority}
                    translate={translate}
                    translatePrefix={translatePrefix}
                />
                <ActivityCreatedAtChange
                    record={record}
                    requestData={requestData}
                    translate={translate}
                    translatePrefix={translatePrefix}
                    isServiceInspection={isServiceInspection}
                />
                <ActivityPersonAssignment
                    record={record}
                    changeType={CHANGE_TYPES.COORDINATOR_ID}
                    changedLabel={translate(`${translatePrefix}.coordinatorChanged`) + ':'}
                    assignedLabel={translate(`${translatePrefix}.coordinatorAssigned`) + ':'}
                    previouslyAssignedPerson={previouslyAssignedPerson}
                    currentlyAssignedPerson={currentlyAssignedPerson}
                />
                <ActivityPersonAssignment
                    record={record}
                    changeType={CHANGE_TYPES.SERVICEMAN_ID}
                    changedLabel={translate(`${translatePrefix}.servicemanChanged`) + ':'}
                    assignedLabel={translate(`${translatePrefix}.servicemanAssigned`) + ':'}
                    previouslyAssignedPerson={previouslyAssignedPerson}
                    currentlyAssignedPerson={currentlyAssignedPerson}
                />
                <ActivityScheduledDateChange translate={translate} translatePrefix={translatePrefix} record={record} />
                <ActivityDifficultyChange translate={translate} translatePrefix={translatePrefix} record={record} />
                {record.changeType === CHANGE_TYPES.USED_PARTS && (
                    <ActivityUsedPartsChange record={record} translate={translate} translatePrefix={translatePrefix} />
                )}
            </>
        );
    }, [
        record,
        previousStatus,
        currentStatus,
        previousType,
        currentType,
        previousPriority,
        currentPriority,
        requestData,
        translate,
        previouslyAssignedPerson,
        currentlyAssignedPerson,
        translatePrefix,
        isServiceInspection,
    ]);

    const renderDetailsContent = useCallback(() => {
        if (!record) return null;

        switch (record.changeType) {
            case CHANGE_TYPES.COMMENT:
                return isEditing ? renderCommentEdit() : renderCommentRead();
            case CHANGE_TYPES.DESCRIPTION:
                return renderDescriptionChange();
            case CHANGE_TYPES.MIGRATED_NOTE:
                return renderMigratedNote();
            case CHANGE_TYPES.WORKING_TIME:
                return renderWorkingTimeChange();
            default:
                return renderOtherChanges();
        }
    }, [record, isEditing, renderCommentEdit, renderCommentRead, renderDescriptionChange, renderMigratedNote, renderWorkingTimeChange, renderOtherChanges]);

    const renderFooter = useCallback(() => {
        if (!record) return null;
        const editedTranslationKey = `${translatePrefix}.edited`;

        return (
            <Box className='footer'>
                <Box className='footer-source'>
                    {record.user && (
                        <>
                            {userRoleIcon}
                            {record.user.firstName} {record.user.lastName}
                        </>
                    )}
                    {record.changeType === CHANGE_TYPES.SYSTEM && <SystemNoteIcon />}
                    {(record.changeType === CHANGE_TYPES.CREATED_AT || record.changeType === CHANGE_TYPES.MIGRATED_NOTE) && record.user === null && (
                        <>
                            <SystemNoteIcon />
                            {translate(`${translatePrefix}.createdBySystem`)}
                        </>
                    )}
                </Box>
                {record.hidden && (
                    <Box className='footer-visibility'>
                        <HiddenIcon />
                        <Typography variant='caption'>{translate(`${translatePrefix}.hiddenNote`)}</Typography>
                    </Box>
                )}
                <Box className='footer-date'>
                    {record.createdAt && (
                        <Typography variant='caption'>
                            {record.changeType === CHANGE_TYPES.COMMENT && record.prevValue && `(${translate(editedTranslationKey)}) `}
                            <Tooltip
                                title={
                                    formatIntervalSince(record.createdAt) ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}` : ''
                                }
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                            </Tooltip>
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }, [record, translatePrefix, userRoleIcon, translate]);

    if (!record) return null;

    const canSeeHiddenComments = isAdmin || isServiceMaster;
    if (record.hidden && !canSeeHiddenComments) {
        return null;
    }

    return (
        <Box sx={activityStyles.activityList}>
            {openConfirm && (
                <Box className='delete-form'>
                    {isLoadingDelete && <CircularProgress />}
                    <SimpleForm id='activity-list-item-delete-form' onSubmit={handleDeleteItem} toolbar={null}>
                        <NotificationBox variant='warningSecondary' className='large'>
                            <Typography variant='body1'>{translate(`${translatePrefix}.deleteActivityConfirm`)}</Typography>
                            <Box className='notification-buttons'>
                                <Button type='button' size='small' variant='contained' onClick={handleCloseConfirm} className='white'>
                                    {translate('common.cancel')}
                                </Button>
                                <Button type='submit' size='small' variant='contained' className='white'>
                                    {translate(`${translatePrefix}.deleteActivityButton`)}
                                </Button>
                            </Box>
                        </NotificationBox>
                    </SimpleForm>
                </Box>
            )}
            <Box className={activityListClass} onClick={isMobile ? () => onToggleActions(record.id) : undefined}>
                {renderActionButtons()}
                <Box className='details' sx={{ '& .MuiCardContent-root': { marginBottom: '0 !important' } }}>
                    {renderDetailsContent()}
                </Box>
                {renderFooter()}
            </Box>
        </Box>
    );
};

ActivityListItem.propTypes = {
    activeCommentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onToggleActions: PropTypes.func.isRequired,
    resource: PropTypes.string.isRequired,
    elementId: PropTypes.string.isRequired,
};

export default ActivityListItem;
